import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRightIcon, PlayIcon } from 'lucide-react';
type VideoEmbedProps = {
  videoId: string;
  title: string;
};

/**
 * YouTube embed that only loads YouTube after the visitor clicks play
 * (privacy-friendly via youtube-nocookie.com, no tracking before interaction).
 */
export function VideoEmbed({
  videoId,
  title
}: VideoEmbedProps) {
  const [playing, setPlaying] = useState(false);
  const [poster, setPoster] = useState(`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`);
  return <div data-id="element-583" data-mp="z36l45t6p7kf">
      <div className="relative overflow-hidden rounded-[16px] bg-graphite p-1.5 shadow-[0_0_0_1px_rgba(70,28,64,0.08),0_30px_60px_-30px_rgba(40,15,35,0.45)] md:p-2" data-id="element-584" data-mp="z2n54un3gt91">
        <div className="relative aspect-video overflow-hidden rounded-[11px] bg-black" data-id="element-585" data-mp="u1zcy5n4q6d8">
          <AnimatePresence initial={false} data-id="element-586" data-mp="1rksjgjhsybny">
            {playing ? <motion.iframe key="player" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`} title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 h-full w-full" data-id="element-587" data-mp="1mrzb7ln4kl74" /> : <motion.button key="poster" type="button" exit={{
            opacity: 0
          }} transition={{
            duration: 0.15
          }} onClick={() => setPlaying(true)} aria-label={`Video abspielen: ${title}`} className="group absolute inset-0 h-full w-full text-left focus-visible:outline-none" data-id="element-588" data-mp="qh9gh6y9w7k3">
                <img src={poster} onError={() => setPoster(`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`)} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:scale-[1.02]" data-id="element-589" data-mp="2av0ez7svzhmf" />
                <span aria-hidden="true" className="absolute inset-0 bg-black/35 transition-colors duration-200 group-hover:bg-black/25" data-id="element-590" data-mp="35tqrzvi4idqd" />

                {/* Play control */}
                <span className="absolute inset-0 grid place-items-center" data-id="element-591" data-mp="6g6k20zi2lk2">
                  <span className="relative grid h-[76px] w-[76px] place-items-center rounded-full bg-white/15 ring-1 ring-white/30 backdrop-blur-md transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:scale-105 group-active:scale-[0.97] group-focus-visible:ring-2 group-focus-visible:ring-white md:h-[92px] md:w-[92px]" data-id="element-592" data-mp="2djz8k18y0wd">
                    <span className="grid h-[54px] w-[54px] place-items-center rounded-full bg-white shadow-[0_8px_24px_-6px_rgba(0,0,0,0.5)] md:h-[66px] md:w-[66px]" data-id="element-593" data-mp="lb2zoz9n2b76">
                      <PlayIcon className="ml-1 h-6 w-6 fill-ink text-ink md:h-7 md:w-7" data-id="element-594" data-mp="sp3ucuc9zgwb" />
                    </span>
                  </span>
                </span>

                {/* Bottom bar */}
                <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-4 md:p-6" data-id="element-595" data-mp="12nbajayqpux9">
                  <span className="text-[15px] font-medium leading-snug text-white md:text-[17px]" data-id="element-596" data-mp="1blmvoxgu80ib">{title}</span>
                  <span className="shrink-0 rounded-full bg-black/40 px-3 py-1.5 text-[12px] text-white/85 backdrop-blur-sm" data-id="element-597" data-mp="1gkg96pw4jrih">
                    Video ansehen
                  </span>
                </span>
              </motion.button>}
          </AnimatePresence>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1 text-[13px] text-muted" data-id="element-598" data-mp="1ucms2jqnwclm">
        <span data-id="element-599" data-mp="1zao6ush21pra">Beim Abspielen wird eine Verbindung zu YouTube hergestellt.</span>
        <a href={`https://youtu.be/${videoId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-ink/70 transition-colors duration-150 hover:text-ink" data-id="element-600" data-mp="1lo8rv7y2ifda">
          Auf YouTube öffnen
          <ArrowUpRightIcon className="h-3.5 w-3.5" data-id="element-601" data-mp="1vgehib0ukkpb" />
        </a>
      </div>
    </div>;
}